import {
  billingHistorySchema,
  billingStateSchema,
  checkoutHandoffSchema,
  type BillingHistory,
  type BillingState,
  type CancelSubscriptionInput,
  type CheckoutHandoff,
  type StartSubscriptionInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Paying for the product (task 24), as distinct from choosing what to pay for (task 23).
 *
 * There is no "confirm payment" call here and there never will be. Checkout answers with a URL
 * at the gateway, the browser goes there, and what happens next is told to the SERVER by a
 * signed webhook. A client-side confirmation would be a shop's own browser asserting that money
 * arrived, which is precisely the claim that must not be taken on trust.
 */

const BILLING = '/billing';

export async function getBillingState(client: ApiClient): Promise<BillingState> {
  return await client.get(BILLING, { schema: billingStateSchema });
}

export async function startCheckout(
  client: ApiClient,
  input: StartSubscriptionInput,
): Promise<CheckoutHandoff> {
  return await client.post(`${BILLING}/checkout`, input, { schema: checkoutHandoffSchema });
}

export async function getBillingHistory(client: ApiClient): Promise<BillingHistory> {
  return await client.get(`${BILLING}/history`, { schema: billingHistorySchema });
}

export async function cancelSubscription(
  client: ApiClient,
  input: CancelSubscriptionInput,
): Promise<BillingState> {
  return await client.post(`${BILLING}/cancel`, input, { schema: billingStateSchema });
}

/**
 * Where the browser fetches one invoice PDF.
 *
 * A path rather than a fetch, because the download is handed to the browser's own machinery —
 * a script-driven save is inert inside the Android WebView a lot of these shops live in.
 */
export function invoicePath(paymentId: string): string {
  return `${BILLING}/history/${paymentId}/invoice`;
}
